import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, SettingsRounded as SettingsIcon } from '@mui/icons-material';
import { BookingConfig } from '../../types';
import { NumberField } from '../Inputs/NumberField';

interface AdvancedSettingsFormProps {
  formData: Partial<BookingConfig>;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string | null>;
  forceExpand?: boolean;
}

export const AdvancedSettingsForm: React.FC<AdvancedSettingsFormProps> = ({
  formData,
  onChange,
  errors,
  forceExpand = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const maxBookingWindow = formData.max_booking_window || 0;
  const days = Math.floor(maxBookingWindow / 1440);
  const hours = Math.floor((maxBookingWindow % 1440) / 60);
  const minutes = maxBookingWindow % 60;

  const handleMaxBookingChange = (field: 'days' | 'hours' | 'minutes', val: string) => {
    const num = val === '' ? 0 : parseInt(val, 10);
    const newDays = field === 'days' ? num : days;
    const newHours = field === 'hours' ? num : hours;
    const newMinutes = field === 'minutes' ? num : minutes;

    const totalMinutes = (newDays * 1440) + (newHours * 60) + newMinutes;
    if (totalMinutes === 0) {
      onChange('max_booking_window', null);
    } else {
      onChange('max_booking_window', totalMinutes);
    }
  };

  useEffect(() => {
    if (forceExpand) {
      setExpanded(true);
    }
  }, [forceExpand]);

  return (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <Accordion
        expanded={expanded}
        onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
        disableGutters
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 4, py: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SettingsIcon color="primary" fontSize="small" />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Advanced Settings
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 4, pb: 4 }}>
          <Typography color="textSecondary" sx={{ mb: 3 }}>
            Configure additional boundaries and offsets for your booking calendar.
          </Typography>

          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} sm={6} data-booking-error-key="first_slot_offset">
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                First Slot Offset Duration *
              </Typography>
              <NumberField
                fullWidth
                label="Minutes"
                value={formData.first_slot_offset ?? ''}
                onChange={(val) => {
                  onChange('first_slot_offset', val === '' ? null : parseInt(val));
                }}
                error={!!errors.first_slot_offset || (
                  formData.first_slot_offset !== undefined && (
                    formData.first_slot_offset === null ||
                    formData.first_slot_offset < 30 ||
                    formData.first_slot_offset % 15 !== 0
                  )
                )}
                helperText={
                  (formData.first_slot_offset === null || (formData.first_slot_offset !== undefined && formData.first_slot_offset < 30))
                    ? 'Minimum 30 min - Required'
                    : (formData.first_slot_offset !== undefined && formData.first_slot_offset % 15 !== 0)
                      ? 'Must be a multiple of 15'
                      : errors.first_slot_offset || 'Minimum buffer before a booking can start'
                }
                min={30}
                step={15}
              />
            </Grid>

            <Grid item xs={12} sm={6} data-booking-error-key="max_booking_window">
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Last Slot Offset Duration *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <NumberField
                  label="Days"
                  value={days || ''}
                  onChange={(val) => handleMaxBookingChange('days', val)}
                  min={0}
                  sx={{ flex: 1 }}
                  error={!!errors.max_booking_window}
                />
                <NumberField
                  label="Hours"
                  value={hours || ''}
                  onChange={(val) => handleMaxBookingChange('hours', val)}
                  min={0}
                  max={23}
                  sx={{ flex: 1 }}
                  error={!!errors.max_booking_window}
                />
                <NumberField
                  label="Minutes"
                  value={minutes || ''}
                  onChange={(val) => handleMaxBookingChange('minutes', val)}
                  min={0}
                  max={59}
                  sx={{ flex: 1 }}
                  error={!!errors.max_booking_window}
                />
              </Box>
              {errors.max_booking_window ? (
                <FormHelperText error sx={{ ml: 1.75 }}>
                  {errors.max_booking_window}
                </FormHelperText>
              ) : (
                <FormHelperText sx={{ ml: 1.75 }}>
                  How far in advance users can book
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.allow_multiple_bookings_per_slot !== false}
                    onChange={(e) => onChange('allow_multiple_bookings_per_slot', e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Multiple Bookings Per Slot
                  </Typography>
                }
              />
              <FormHelperText sx={{ ml: 1.75 }}>
                Let multiple users book the same time slot
              </FormHelperText>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};
